import React from 'react'
import localforage from 'myStore'
import i18n from 'i18n'
import parse from 'html-react-parser'
import history from 'history'
import { omNotification } from '@owma/kit'

import { decodeJwtPayload, getSearchParams, makeSearchString } from 'utils'
import { confirmBase, errorBase, checkOpenedWindow, openloadingAni } from 'utils/ui'
import NativeInterface from 'services/native-interface/om-native-client'

import { store as reduxStore } from 'index'
import {
  setEnvParam,
  getEnvParam,
  removeEnvParam,
  getTempEnvParam,
  setTempEnvParam,
  removeTempEnvParam,
} from 'env'

import { verifyOtpCode } from 'services/admin-otp'
import settingSyncService from 'services/settingSync'
import { isPopupMode } from 'services/popup/isPopupMode'

import { restLandingClient, officeNextClient, authClient, authClientWithToken } from 'myNet'
import { classifyEntryError } from './entryError'
import { decideEntryTokenReuse } from './entryReuse'

export type { EntryLoginError, EntryLoginResult } from './entryError'
import type { EntryLoginResult } from './entryError'
import mailboxApiHelper from 'services/mailbox-api'
import { getDeviceUUID } from 'pUtils'

import dataMgr from 'pages/main/data/DataManager'
import signatureAPI from 'pages/main/data/api/SignatureAPI'

import warningIcon from 'assets/images/exclamation-circle-red-22.svg'

import {
  initClient as initCalendarClient,
  logout as logoutCalendar,
} from 'pages/calendar/modules/CalendarService'
import { logoutAndClearData as logoutTodo } from 'pages/todo/modules/TodoService'
import {
  initClient as initContactClient,
  logout as logoutContact,
} from 'pages/contacts/modules/contactService'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import orgChartHelper from 'pages/contacts/components/OrgChart/Helper'
import { checkOffline } from 'popups'

import {
  login as loginJMAP,
  currentAccount as currentAccountJMAP,
  logout as logoutJMAP,
} from 'services/jmap'

import { sentrySetUser } from 'services/sentry'

import {
  getMyInfo,
  getUserExists,
  getWorkspaceInfo,
  // getWorkspaceList
  getWorkspaceConfig,
  mutateUpdateDevice,
} from './api'

import { ClientJS } from 'clientjs'

import { sendEncrypted } from 'pUtils'
import settings from 'popups/Settings'

import conf from 'conf'

interface PrevLandingInfo {
  clearLandingEvent?: (() => void) | null
}

interface AppConfigMap {
  [email: string]: AppConfig
}

interface AppConfig {
  officenext: boolean
  AppAuthenticationMethod: string
  AppEmailServiceProvider: string
  workspace_id?: string
  AppServiceHosts?: string
  AppServicePort?: number
}

interface AppConfigInfo {
  officenext?: boolean
  auth?: string
  provider?: string
  useOTP?: boolean
  authUrl?: string
}

export interface OfficeNextPassInfo {
  authUrl: string
  state?: string
  codeVerifier?: string
}

interface LandingData {
  params: {
    result: string
  }
  status?: string
  error?: any
}

interface LandingDataForLogin extends LandingData {
  params: {
    result: string
    email?: string
    landing_email?: string
    username?: string
    workspace_id?: string
    jwt?: {
      accessToken: string
      refreshToken: string
      expiresIn?: number
    }
    officenext?: {
      accessToken: string
    }
    code?: string
    message?: string
    // 605 서비스 접근 불가 판별용 에러 코드(예: SERVICE_ACCESS_DENIED). (OFM-242)
    error_code?: string
    // 605 SERVICE_ACCESS_DENIED 시 OfficeNEXT 로그아웃용 토큰. (OFM-242)
    provider_token?: string
  }
}

interface TokenInfo {
  provider_token?: string
  access_token?: string
  refresh_token?: string
}

interface MyInfo {
  id?: string
  email: string
  display_name?: string
  avatar_url?: string
}

interface WorkspaceConfig {
  enable_officemail?: boolean
  enable_org_chart?: boolean
  crm_contacts?: any
  shared_contacts?: any
  priority_inbox?: any
  ai?: any
  spam_sniper?: {
    spam_folder?: boolean
  }
}

interface WorkspaceInfo {
  role: string
  workspaceGid?: string
  workspaceName?: string
  workspaceEmail?: string
  iconUrl?: string
  provider?: string
  enableChat?: boolean
  orgChartType?: string
}

interface Services {
  source: string | null
  pages: {
    email?: boolean
    contacts?: boolean
    calendar?: boolean
    todo?: boolean
  }
  ai?: any
  can_use_email: boolean
  enable_orgchart?: boolean
  crm_contacts?: any
  shared_contacts?: boolean
  priority_inbox?: any
  spam_sniper?: boolean
}

interface ServicePreset {
  pages: {
    email: boolean
    contacts: boolean
    calendar: boolean
    todo: boolean
  }
  crm_contacts: { editable_by_user: boolean }
  can_use_email: boolean
  enable_orgchart?: boolean
}

interface ProviderInfo {
  authType: string
  provider: string
  protocol: string
}

interface RegisterParams {
  username: string
  email: string
  password: string
  deviceModel: string
  deviceUUID: string
  appCategory: string
  workspaceId: string
  ewsURI?: string
  authType: string
  provider: string
  protocol: string
}

interface ParsedToken {
  email?: string
  account?: string
  exp?: number
  services?: string[]
  workspace_id?: string
  sub?: string
}

interface CurrentAccountResult {
  id?: string
  name: string
  email: string
  uid: string
  avatar: string
  role: string
  lastLogin: string
  workspace: WorkspaceInfo
}

const prevLandingInfo: PrevLandingInfo = {}

const appConfigMap: AppConfigMap = {}

const accountNotFoundError = (): void => {
  const text = i18n.t('login.desc.notOfficenext')
  errorBase({
    title: i18n.t('login.subtitle.notOfficenext'),
    content: parse(text),
  })
}

export function appConfig(email: string, forceOfficemail?: boolean): Promise<AppConfigInfo> {
  const promise = new Promise<AppConfigInfo>((resolve, reject) => {
    const info: AppConfigInfo = {}

    const landingOfficemail = () => {
      const landingType = 'officemail'
      const groupServiceCode = 'officemail'
      const provider = 'officenext'

      authClient
        .get(`/auth/oauth/authorize/${groupServiceCode}/${provider}`, {
          login_hint: email,
          response_mode: 'redirect',
        })
        .then((landingRes) => {
          console.log('landing - ', landingType, landingRes)
          if (landingRes.data.code === 200) {
            console.log(
              'landingRes.data.data.authorization_url - ',
              landingRes.data.data.authorization_url,
            )
            info.authUrl = landingRes.data.data.authorization_url
            resolve(info)
          } else {
            reject(new Error('landing error.'))
          }
        })
    }

    if (forceOfficemail !== true) {
      restLandingClient
        .post('/provisioning/appconfig', {
          email,
        })
        .then(async (res) => {
          console.log(' res - ', res)
          const { data } = res
          if (data.code === 200) {
            const appconfigs = data.data.appconfig
            if (appconfigs.length > 0) {
              const [appconfig] = appconfigs

              const { officenext, AppAuthenticationMethod, AppEmailServiceProvider } = appconfig

              appConfigMap[email] = appconfig

              // localforage.setItem('app.user.appconfig', appconfig);

              info.officenext = officenext
              info.auth = AppAuthenticationMethod.toLowerCase()
              info.provider = AppEmailServiceProvider.toLowerCase()
              info.useOTP = false

              if (appconfig.officenext === true) {
                // officenext인 경우에는 강제로 landing을 호출.
                info.officenext = true
                info.auth = 'oauth2'
                info.provider = 'officemail'
                info.useOTP = false
                landingOfficemail()
              } else {
                // if (info.auth !== 'basic') {
                //   // officenext가 아니면 basic auth를 제외하고 모두 otp.
                //   info.useOTP = true
                // }
                // resolve(info)

                // officenext가 아니면 무조건 에러창 표시.
                accountNotFoundError()
                reject(new Error('This is not an OfficeNext account.'))
              }
            } else {
              errorBase({
                content: 'appconfig error.',
              })
              reject(new Error('appconfig error.'))
            }
          } else if (data.message != null) {
            accountNotFoundError()
            reject(new Error(data.message))
          }
        })
        .catch((err) => {
          console.log(' err - ', err)
          reject(err)
        })
    } else {
      landingOfficemail()
    }
  })

  return promise
}

export function officeNextPass(): Promise<OfficeNextPassInfo> {
  const promise = new Promise<OfficeNextPassInfo>((resolve, reject) => {
    const info: OfficeNextPassInfo = {
      authUrl: '',
    }

    const landingOfficemail = () => {
      const groupServiceCode = 'officemail'
      const provider = 'officenext_pass_zero'

      authClient
        .get(`/auth/oauth/authorize/${groupServiceCode}/${provider}`, {
          response_mode: 'redirect',
        })
        .then((landingRes) => {
          console.log('landing - officeNextPass', landingRes)
          const { data, code } = landingRes.data
          if (code === 200) {
            console.log('landingRes.data.data.authorization_url - ', data.authorization_url)
            info.authUrl = data.authorization_url
            info.state = data.state
            info.codeVerifier = data.code_verifier
            resolve(info)
          } else {
            reject(new Error('landing error.'))
          }
        })
        .catch((e) => {
          reject(new Error(e.message))
        })
    }

    landingOfficemail()
  })

  return promise
}

export function setApiURL(appConfig?: AppConfig): void {
  if (appConfig != null) {
    setEnvParam('apiURL', appConfig.AppServiceHosts)
  } else {
    if (__ENV_PRODUCTION__) {
      setEnvParam('apiURL', 'mail.officemail.io')
    } else {
      setEnvParam('apiURL', 'mail.officemail.app')
    }
  }
}

interface WarningOptions {
  title?: string | null
  content: any
  width?: number
  icon?: any
}

const warning = (options: WarningOptions): any => {
  const props: any = {
    title: options.title,
    content: options.content,
    width: 450,
  }

  if (options.icon !== undefined) {
    props.icon = options.icon
  }

  return errorBase(props)
}

const clearPrevLandingEvent = (): void => {
  if (prevLandingInfo.clearLandingEvent != null) {
    prevLandingInfo.clearLandingEvent()
  }
}

interface WindowSize {
  width: number
  height: number
}

const getRandingWindowSize = (): WindowSize => {
  return {
    width: Math.min(window.screen.availWidth, 1024),
    height: Math.min(window.screen.availHeight, 711),
  }
}

// 팝업 윈도우를 연다. 로그인 외 팝업 기반 기능에서도 재사용 가능한 범용 함수.
// windowSize를 지정하지 않으면 현재 설정된 기본 크기로 연다.
export function openLandingPopup(windowSize?: WindowSize): boolean {
  // 이전에 등록된 message 이벤트를 제거한다.
  clearPrevLandingEvent()

  const baseSize = getRandingWindowSize()
  const size = windowSize ?? baseSize

  // 위치는 기본(전체) 크기 기준으로 중앙 정렬한다.
  // 작은 크기로 먼저 연 뒤 resizeTo로 키워도 중앙에 위치하도록 하기 위함.
  const windowLocation = {
    left: (window.screen as any).availLeft + window.screen.availWidth / 2 - baseSize.width / 2,
    top: (window.screen as any).availTop + window.screen.availHeight / 2 - baseSize.height / 2,
  }

  const options = `width=${size.width},height=${size.height},left=${windowLocation.left},top=${windowLocation.top}`

  const winName = `landingWin`

  // 이미 열려있는 landing 팝업이 있으면 재사용해 focus만 한다.
  // close 후 재오픈은 깜박임을 만들고 SSO 진행 상태도 잃게 한다.
  // (호출부에서 후속 startLanding이 landingWin.location.href = authUrl로 새 URL을 적용)
  const existing = (window as any).landingWin as Window | null | undefined
  if (existing != null && !existing.closed) {
    try {
      existing.focus()
    } catch (e) {
      console.log('focus fail.', e)
    }
    return true
  }

  ;(window as any).landingWin = window.open('', winName, options)
  checkOpenedWindow((window as any).landingWin)

  if ((window as any).landingWin == null) {
    warning({
      title: i18n.t('common.title.blockPopup'),
      content: i18n.t('common.warning.blockPopup'),
    })

    return false
  }

  return true
}

// 로그인 전용 팝업 열기. smallSize=true면 Safari 팝업 차단 회피용 10x10 팝업을 먼저 연다.
// 팝업이 차단되면 로그인을 중단하기 위해 LOGOUT을 dispatch한다.
export function openLandingPopupForLogin(smallSize?: boolean): boolean {
  const opened = openLandingPopup(smallSize === true ? { width: 10, height: 10 } : undefined)

  if (opened !== true) {
    reduxStore.dispatch({
      type: 'user/LOGOUT',
    })

    return false
  }

  return true
}

// 팝업 윈도우를 닫는 범용 함수.
export function closeLandingPopup(): void {
  try {
    if ((window as any).landingWin != null) {
      ;(window as any).landingWin.close()
      ;(window as any).landingWin = null
    }
  } catch (err) {
    console.log(err)
  }
}

// 로그인 전용 팝업 닫기. 닫힘 감지용 loginTimer를 정리한 뒤 팝업을 닫는다.
export function closeLandingPopupForLogin(): void {
  if ((window as any).loginTimer != null) {
    clearInterval((window as any).loginTimer)
  }

  closeLandingPopup()
}

export function parseJwt(token: string): string {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  )
  return JSON.parse(jsonPayload).email
}

function notMatchingAccount(tokenEmail: string): any {
  const cmdObj: any = {
    type: 'user/LOGOUT',
  }
  let newParams = ''
  const searchParams = getSearchParams()
  let target = null
  if (searchParams.params != null && typeof searchParams.params === 'string') {
    const sparams = JSON.parse(atob(searchParams.params))
    if (searchParams.path != null) {
      sparams.path = searchParams.path
    }
    if (Object.keys(sparams).length > 0) {
      newParams = makeSearchString(sparams)
      if (sparams.email != null) {
        target = `/auth/redirect?${newParams}`
      } else {
        target = `/auth/login?${newParams}`
      }
    }
  }

  return confirmBase({
    icon: React.createElement('img', { src: warningIcon, alt: 'warning' }),
    width: 450,
    title: i18n.t('login.button.differentAccount'),
    content: i18n.t('login.error.notmatchingOfficeNext', { email: tokenEmail }),
    okText: i18n.t('logOut.title.logout'),
    onOk: () => {
      reduxStore.dispatch(cmdObj)
      if (target != null) {
        history.push(target)
      }
    },
    onCancel: () => {
      window.location.reload()
    },
  })
}

// landing 결과(OK) 처리 시 토큰 일치 여부에 따른 처리 결과.
type LoginResultOutcome = 'success' | 'not-matching' | 'no-token'

interface ProcessLoginResultOptions {
  // 로그인 시작 시점에 이미 알고 있는 이메일. 팝업 로그인은 입력한 이메일, officeNextPass는 null.
  knownEmail?: string | null
  // 토큰 이메일과 비교할 대상 이메일. 불일치 시 계정 불일치 처리.
  matchEmail?: string | null
  // params.username으로 userEmail을 갱신할지 여부. (팝업 로그인 전용)
  handleUsername?: boolean
  // 임시 appConfig를 항상 덮어쓸지(true) 없을 때만 설정할지(false) 여부.
  overwriteAppConfig?: boolean
  // 토큰 저장 후 app.callback.state를 설정할지 여부. (officeNextPass 전용)
  setCallbackState?: boolean
  // device/register 호출 시 knownEmail 대신 결과로 확정된 이메일을 사용할지 여부.
  registerWithResolvedEmail?: boolean
  // LOGIN_BY_TOKEN dispatch 직전에 실행할 추가 처리. (팝업 로그인의 message 리스너 해제 등)
  onBeforeDispatch?: () => void
}

// landing 결과(result === 'OK')의 공통 로그인 처리.
// 팝업 방식(startLandingForLogin)과 redirect 방식(processOfficeNextPass)이 공유한다.
async function processLoginResult(
  data: LandingDataForLogin,
  options: ProcessLoginResultOptions,
): Promise<LoginResultOutcome> {
  const { params } = data
  console.log(' -- params : ', params)

  let resolvedEmail: string | null = options.knownEmail ?? null
  if (resolvedEmail != null) {
    setEnvParam('userEmail', resolvedEmail)
  } else if (params.email != null) {
    // email이 null인 경우에는 officeNextPass로 로그인한 것임.
    // 이 경우에는 officenext 인증이므로, appConfig를 따로 호출하지 않고 임시 데이터로 대체함.
    if (options.overwriteAppConfig === true || appConfigMap[params.email] == null) {
      appConfigMap[params.email] = {
        officenext: true,
        AppAuthenticationMethod: '',
        AppEmailServiceProvider: 'officemail',
      }
    }

    resolvedEmail = params.email
    setEnvParam('userEmail', params.email)
  }

  if (options.handleUsername === true && params.username != null) {
    setEnvParam('userEmail', params.username)
  }

  const workspaceId = params.workspace_id
  if (workspaceId != null) {
    setTempEnvParam('workspaceId', workspaceId)
    localforage.setItem('app.user.workspace_id', workspaceId)
  }

  if (params.jwt == null) {
    return 'no-token'
  }

  const tokenEmail = parseJwt(params.jwt.accessToken)
  if (options.matchEmail != null && tokenEmail !== options.matchEmail) {
    await localforage.setItem('app.user.providerToken', params.officenext!.accessToken)
    notMatchingAccount(tokenEmail)
    return 'not-matching'
  }

  if (params.jwt.accessToken == null) {
    return 'no-token'
  }

  setTempEnvParam('token', params.jwt.accessToken)
  setTempEnvParam('refreshToken', params.jwt.refreshToken)

  await localforage.setItem('app.user.token', params.jwt.accessToken)
  await localforage.setItem('app.user.refreshToken', params.jwt.refreshToken)
  await localforage.setItem('app.user.jmap.tokenType', 'Bearer')
  await localforage.setItem('app.user.jmap.token', params.jwt.accessToken)
  await localforage.setItem('app.user.providerToken', params.officenext!.accessToken)

  const parsedCommonToken = decodeJwtPayload(params.jwt.accessToken)
  if (parsedCommonToken != null) {
    await localforage.setItem('app.user.expiresIn', parsedCommonToken.exp)
  }

  if (options.setCallbackState === true) {
    setTempEnvParam('app.callback.state', true)
  }

  const registerEmail =
    options.registerWithResolvedEmail === true ? resolvedEmail : (options.knownEmail ?? null)

  const finish = () => {
    if (options.onBeforeDispatch != null) {
      options.onBeforeDispatch()
    }

    updateDevice(registerEmail as string) // 성공 여부와 상관없으므로 비동기로 처리하고 결과에 따른 처리는 하지 않음.

    reduxStore.dispatch({
      type: 'user/LOGIN_BY_TOKEN',
      payload: {
        tokenInfo: {
          provider_token: params.officenext!.accessToken,
          access_token: params.jwt!.accessToken,
        },
      },
    })
  }

  let userExists: boolean = false
  try {
    userExists = await getUserExists()
    console.log('userExists', userExists)
  } catch (e) {
    console.log('userExists error', e)
  }

  if (userExists !== true) {
    try {
      const res = await authRegister(registerEmail as string)
      console.log('[authRegister] success: ', res)
    } catch (err) {
      console.error('[authRegister] error: ', err)
    }
  }

  finish()

  return 'success'
}

// landing 팝업 origin 검증. 현재 origin과 다르고, rework.so 또는 officemail.io 도메인인 경우만 허용.
const isAllowedLandingOrigin = (origin: string): boolean => {
  if (window.location.origin === origin) {
    return false
  }

  return origin.indexOf('rework.so') >= 0 || origin.indexOf('officemail.io') >= 0
}

interface StartLandingOptions<T extends LandingData> {
  windowSize?: WindowSize
  // 팝업을 닫는 함수. 기본값은 범용 closeLandingPopup.
  closePopup?: () => void
  // origin 허용 여부 판별 함수. 기본값은 isAllowedLandingOrigin.
  isAllowedOrigin?: (origin: string) => boolean
  // result === 'OK' 수신 시의 추가 처리.
  onResult?: (data: T) => Promise<void> | void
  // 사용자가 팝업을 닫았을 때의 처리. 지정하지 않으면 reject한다.
  onClose?: () => void
}

// 팝업을 열어 landing 메시지를 수신하는 범용 함수.
// OK 메시지를 받으면 파싱된 데이터를 resolve하고, 에러 메시지는 reject한다.
// 사용자가 팝업을 닫으면 onClose를 실행하거나(미지정 시) reject한다.
export function startLanding<T extends LandingData = LandingData>(
  authUrl: string,
  options: StartLandingOptions<T> = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let closeWatchTimer: ReturnType<typeof setInterval> | null = null

    function cleanup(): void {
      window.removeEventListener('message', onLandingMessage)
      if (closeWatchTimer != null) {
        clearInterval(closeWatchTimer)
        closeWatchTimer = null
      }
    }

    async function onLandingMessage(e: MessageEvent): Promise<void> {
      console.log(' -- message : ', e)

      const allowed = options.isAllowedOrigin ?? isAllowedLandingOrigin
      if (!allowed(e.origin)) {
        return
      }

      const data = JSON.parse(e.data) as T

      ;(options.closePopup ?? closeLandingPopup)()

      if (data.params.result === 'OK' || data.status === 'success') {
        if (options.onResult != null) {
          await options.onResult(data)
        }
        cleanup()
        resolve(data)
      } else if (data.error != null) {
        console.log(' --- landing error ', data)
        const { code, message } = data.error
        const error: any = new Error(message)
        error.code = code
        cleanup()
        reject(error)
      } else {
        cleanup()
        reject(new Error('message parameter error.'))
      }
    }

    window.addEventListener('message', onLandingMessage, false)

    prevLandingInfo.clearLandingEvent = () => {
      cleanup()
      prevLandingInfo.clearLandingEvent = null
    }

    const size = options.windowSize ?? getRandingWindowSize()
    try {
      ;(window as any).landingWin.resizeTo(size.width, size.height)
    } catch (e) {
      console.log('resize fail.', e)
    }

    ;(window as any).landingWin.location.href = authUrl

    closeWatchTimer = setInterval(() => {
      if ((window as any).landingWin?.closed) {
        cleanup()
        if (options.onClose != null) {
          options.onClose()
        } else {
          reject(new Error('landing popup closed.'))
        }
      }
    }, 1000)
  })
}

// 로그인 전용 landing. OK 수신 시 공통 로그인 처리를 수행하고,
// 사용자가 팝업을 닫으면 LOGOUT을 dispatch한다. (closeLandingPopupForLogin으로 loginTimer 정리)
export function startLandingForLogin(email: string | null, authUrl: string): Promise<null> {
  const promise = new Promise<null>((resolve, reject) => {
    const onSuccessLanding = async (e: MessageEvent) => {
      console.log(' -- message : ', e)

      if (!isAllowedLandingOrigin(e.origin)) {
        return
      }

      const data = JSON.parse(e.data) as LandingDataForLogin

      closeLandingPopupForLogin()

      if (data.params.result === 'OK') {
        await processLoginResult(data, {
          knownEmail: email,
          matchEmail: email,
          handleUsername: true,
          overwriteAppConfig: true,
          onBeforeDispatch: () => window.removeEventListener('message', onSuccessLanding),
        })

        window.removeEventListener('message', onSuccessLanding)
        resolve(null)
      } else if (data.error != null) {
        console.log(' --- login error ', data)
        const { code, message } = data.error
        const error: any = new Error(message)
        error.code = code
        window.removeEventListener('message', onSuccessLanding)
        reject(error)
      } else {
        reject(new Error('message parameter error.'))
      }
    }

    window.addEventListener('message', onSuccessLanding, false)

    prevLandingInfo.clearLandingEvent = () => {
      window.removeEventListener('message', onSuccessLanding)
      // 이전 호출의 loginTimer가 남아 새 팝업에서 잘못된 LOGOUT을 dispatch하는 것을 방지.
      if ((window as any).loginTimer != null) {
        clearInterval((window as any).loginTimer)
        ;(window as any).loginTimer = null
      }
      prevLandingInfo.clearLandingEvent = null
    }

    const windowSize = getRandingWindowSize()
    try {
      ;(window as any).landingWin.resizeTo(windowSize.width, windowSize.height)
    } catch (e) {
      console.log('resize fail.', e)
    }

    ;(window as any).landingWin.location.href = authUrl
    ;(window as any).loginTimer = setInterval(() => {
      if ((window as any).landingWin?.closed) {
        clearInterval((window as any).loginTimer)

        reduxStore.dispatch({
          type: 'user/LOGOUT',
        })
      }
    }, 1000)
  })

  return promise
}

export function processOfficeNextPass(dataStr: string): Promise<null> {
  const promise = new Promise<null>(async (resolve, reject) => {
    const data = JSON.parse(dataStr) as LandingDataForLogin

    if (data.params.result === 'OK') {
      const outcome = await processLoginResult(data, {
        knownEmail: null,
        matchEmail: data.params.landing_email ?? null,
        handleUsername: false,
        overwriteAppConfig: false,
        setCallbackState: true,
        registerWithResolvedEmail: true,
      })

      if (outcome === 'not-matching') {
        reject(new Error('not matching account.'))
        return
      }

      resolve(null)
    } else if (data.error != null) {
      console.log(' --- login error ', data)
      const { code, message } = data.error
      const error: any = new Error(message)
      error.code = code
      reject(error)
    } else {
      reject(new Error('message parameter error.'))
    }
  })

  return promise
}

export function getOfficeNextPassError(errorStr: string): any {
  const data = JSON.parse(errorStr) as LandingDataForLogin

  if (data.error != null) {
    console.log(' --- login error ', data)
    const { code, message, error_code, provider_token } = data.params
    const error: any = new Error(message)
    error.code = code
    // 605 서비스 접근 불가 판별(error_code) 및 OfficeNEXT 로그아웃(provider_token)에 사용. (OFM-242)
    error.errorCode = error_code
    error.providerToken = provider_token

    return error
  }

  return null
}

export function openChangePassword(): Promise<void> {
  const promise = new Promise<void>(async (resolve, reject) => {
    try {
      const client = new ClientJS()
      // WKWebView(Tauri 등)는 UA 에 "Safari" 토큰이 없어 clientjs.isSafari() 가 false 를 반환한다.
      // getBrowser() 결과(ua-parser browser.name)가 Safari/WebKit 계열이면 Safari 로 간주한다.
      const isSafari = /safari|webkit/i.test(client.getBrowser())

      if (isSafari !== true || openLandingPopup({ width: 10, height: 10 }) === true) {
        const res = await authClientWithToken.post('/auth/password-change-url', {
          officenext_access_token: await localforage.getItem('app.user.providerToken'),
          redirect_uri: `${conf.SERVER.authURL}/auth/password-change-callback`,
        })
        if (res.data.code === 200) {
          const changePasswordUrl = res.data.data.password_change_url
          if (isSafari === true || openLandingPopup({ width: 550, height: 700 }) === true) {
            startLanding(changePasswordUrl, {
              closePopup: closeLandingPopup,
              isAllowedOrigin: (origin) => conf.SERVER.authURL.indexOf(origin) >= 0,
              onResult: async (data) => {
                if (data.params.result === 'OK' || data.status === 'success') {
                  omNotification.success({
                    message: i18n.t('settings.changePasswordSuccess'),
                    description: i18n.t('settings.changePasswordSuccessDesc'),
                  })

                  resolve()
                } else {
                  omNotification.error({
                    message: i18n.t('settings.changePasswordFailed'),
                    description: i18n.t('settings.changePasswordFailedDesc'),
                  })
                }
              },
              onClose: () => {},
              windowSize: { width: 550, height: 700 },
            })
          } else {
            reject(new Error('Failed to open popup window.'))
          }
        } else {
          reject(new Error('Failed to get change password URL.'))
        }
      } else {
        reject(new Error('Failed to open popup window.'))
      }
    } catch (e) {
      reject(e)
    }
  })

  return promise
}

// authRegister 이후에 디바이스 정보를 업데이트한다. 실패하더라도 로그인에는 영향을 주지 않도록 한다.
// 원래는 firebase cloud messaging 처리를 위해 사용되었으나, 현재는 단순히 단말기 정보를 등록하기 위해 사용.
async function updateDevice(email: string): Promise<void> {
  try {
    const deviceUUID = await getDeviceUUID(email)
    const workspaceId = getTempEnvParam('workspaceId')
    const client = new ClientJS()

    let os = ''
    if (client.isMac()) {
      os = 'mac'
    } else if (client.isWindows()) {
      os = 'windows'
    } else {
      os = 'linux'
    }

    const deviceModel = 'x86_64'
    const appVersion = import.meta.env.VITE_APP_VERSION!

    await mutateUpdateDevice({
      vars: {
        deviceUuid: deviceUUID,
        workspaceId,
        deviceData: {
          deviceUuid: deviceUUID,
          // fcmToken: null,
          type: 'web',
          name: deviceModel,
          os,
          osVersion: client.getOSVersion(),
          appVersion,
        },
      },
    })
  } catch (e) {
    console.warn('Error occurred while updating device:', e)
  }
}

function authRegister(email: string): Promise<boolean> {
  const promise = new Promise<boolean>(async (resolve, reject) => {
    const appConfig = appConfigMap[email]
    //  || (await localforage.getItem('app.user.appconfig')) as AppConfig
    // await localforage.removeItem('app.user.appconfig')

    const { workspace_id, AppEmailServiceProvider, AppAuthenticationMethod, AppServiceHosts } =
      appConfig

    const workspaceId = workspace_id || getTempEnvParam('workspaceId')
    const deviceUUID = await getDeviceUUID(email)
    const deviceModel = 'web'

    let protocol = 'imap'
    let authType = AppAuthenticationMethod
    const type = AppEmailServiceProvider.toLowerCase()
    switch (type) {
      case 'ms365':
        authType = 'oauth'
        protocol = 'graph' // graph or ews
        break
      case 'gmail':
        authType = 'oauth'
        protocol = 'http'
        break
      case 'imap':
        authType = 'basicauth'
        protocol = 'imap'
        break
      case 'officemail':
        authType = 'oauth'
        protocol = 'imap'
        break
      default:
        protocol = 'imap'
    }

    const providerInfo: ProviderInfo = {
      authType,
      provider: AppEmailServiceProvider,
      protocol,
    }

    const params: RegisterParams = {
      username: email, // display_name 아님. email을 사용해야 함.
      email,
      password: '',
      deviceModel,
      deviceUUID,
      appCategory: 'enterprise', // store
      workspaceId: workspaceId,
      ewsURI: AppServiceHosts,
      ...providerInfo,
    }

    sendEncrypted(
      '/auth/register/web/',
      params,
      async (res: any) => {
        if (res.code === 200) {
          resolve(true)
        } else {
          resolve(false)
        }
      },
      (ex: any) => {
        console.log(ex)
        omNotification.warning({
          message: ex.code,
          description: ex.message,
        })
        reject(ex)
      },
    )
  })

  return promise
}

async function loginIMAP(email: string, password: string): Promise<string> {
  const promise = new Promise<string>((resolve, reject) => {
    const appConfig = appConfigMap[email]

    const params: any = {
      email,
      password,
    }

    if (appConfig != null) {
      setApiURL(appConfig)
      params.host = appConfig.AppServiceHosts
      params.port = appConfig.AppServicePort
    } else {
      const autoDiscoverRes = getEnvParam('providerInfo')
      params.host = autoDiscoverRes.incomingServer.hostname
      params.port = autoDiscoverRes.incomingServer.port
    }

    restLandingClient
      .post(`/auth/verifying/imap`, params)
      .then((res) => {
        console.log('data - ', res.data)

        if (res.data.code === 200) {
          setEnvParam('userEmail', email)

          authRegister(email)
            .then((/*res2*/) => {
              resolve('success')
            })
            .catch((err) => {
              reject(err)
            })
        } else if (res.data.code === 401) {
          errorBase({
            title: null,
            content: i18n.t('login.error.emailOrPwInvalid'),
          })
        } else if (res.data.code === 500 || res.data.code === 504) {
          errorBase({
            title: i18n.t('login.error.internalError'),
            content: i18n.t('login.error.retry'),
          })
        } else {
          reject(new Error('login failed'))
        }
      })
      .catch((error) => {
        console.log('error - ', error)
        errorBase({
          title: error.code,
          content: error.message,
        })
        reject(error)
      })
  })

  return promise
}

export async function login(email: string, password?: string): Promise<any> {
  console.log(' login - ', email)
  if (password != null) {
    const result = await loginIMAP(email, password)
    if (result === 'success') {
      const appConfig = appConfigMap[email]

      if (appConfig != null) {
        setApiURL(appConfig)
      }

      return loginJMAP(email, password)
    }
  }

  return null
}

export async function loginByOTP(email: string, loginCode: string): Promise<boolean> {
  const promise = new Promise<boolean>((resolve) => {
    verifyOtpCode(email, loginCode)
      .then(async (res) => {
        const { data } = res
        console.log('data - ', data)

        if (data.code === 200) {
          if (data.workspace_id != null) {
            await localforage.setItem('app.user.workspace_id', data.workspace_id)
          } else if (appConfigMap[email] != null && appConfigMap[email].workspace_id != null) {
            await localforage.setItem('app.user.workspace_id', appConfigMap[email].workspace_id)
          }

          if (data.access_token != null) {
            await localforage.setItem('app.user.token', data.access_token)
          }

          if (data.refresh_token != null) {
            await localforage.setItem('app.user.refreshToken', data.refresh_token)
          }

          const appConfig = appConfigMap[email]

          if (appConfig != null) {
            setApiURL(appConfig)
          }

          resolve(true)
        } else {
          if (data.code === 401) {
            errorBase({
              title: null,
              content: i18n.t('login.error.emailOrPwInvalid'),
            })
          } else if (data.code === 500 || data.code === 504) {
            errorBase({
              title: i18n.t('login.error.internalError'),
              content: i18n.t('login.error.retry'),
            })
          } else {
            errorBase({
              title: 'Error',
              content: 'login failed',
            })
          }
          resolve(false)
        }
      })
      .catch((error) => {
        console.log('error - ', error)
        errorBase({
          title: error.code,
          content: error.message,
        })
        resolve(false)
      })
  })

  return promise
}

export async function loginByKey(/*key*/): Promise<string> {
  const promise = new Promise<string>(async (resolve) => {
    try {
      resolve('success')
    } catch (error: any) {
      console.log('error - ', error)
      errorBase({
        title: error.code,
        content: error.message,
      })
      resolve('fail')
    }
  })

  return promise
}

export async function register(/* email, password, name */): Promise<boolean> {
  function fakeRegister(): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true)
      }, 200)
    })
  }

  return fakeRegister()
}

export function loginByToken(info: TokenInfo): Promise<string> {
  const promise = new Promise<string>(async (resolve) => {
    if (info.provider_token != null) {
      const userEmail = getEnvParam('userEmail')

      await localforage.setItem('app.user.providerToken', info.provider_token)

      const appConfig = appConfigMap[userEmail]

      if (appConfig?.officenext === true) {
        // package 정보 - free, pro
        await localforage.setItem('app.user.officenext', appConfig.officenext)
      } else {
        await localforage.removeItem('app.user.officenext')
      }

      if (appConfig != null) {
        setApiURL(appConfig)
      }
    }

    resolve('success')
  })

  return promise
}

export function loginByTokenFromDesktop(
  accessToken: string,
  path: string,
  additionalParams: string[],
): Promise<boolean> {
  const promise = new Promise<boolean>(async (resolve) => {
    if (NativeInterface.isAvailable() !== true) {
      resolve(false)
      return
    }

    try {
      const email = additionalParams.find((param) => param.startsWith('email='))?.split('=')[1]
      const workspaceId = additionalParams
        .find((param) => param.startsWith('workspace_id='))
        ?.split('=')[1]

      // 1. 토큰 유효성 검증
      if (!accessToken) {
        console.error('Missing access_token')
        resolve(false)
        return
      }

      // 2. URL 파라미터 검증
      if (!email || !workspaceId) {
        console.error('Missing email or workspace_id from query parameters')
        resolve(false)
        return
      }

      // 3. JWT 파싱 (exp, sub, services 추출용)
      const parsedToken = decodeJwtPayload(accessToken)

      // 3-1. 토큰 검증
      if (!parsedToken) {
        console.error('Token parsing failed')
        resolve(false)
        return
      }

      await localforage.setItem('app.user.isDesktopSession', true)

      // 6. localforage에 원본 토큰 저장
      await localforage.setItem('app.user.token', accessToken)
      await localforage.setItem('app.user.tokenType', 'JWT')

      // 5. URL 쿼리 파라미터에서 받은 정보 저장
      // 사용자 이메일 저장
      setEnvParam('userEmail', email)
      setEnvParam('userInternalEmail', email)

      // 워크스페이스 ID 저장
      setTempEnvParam('workspaceId', workspaceId)
      await localforage.setItem('app.user.workspace_id', workspaceId)

      // 6. JWT에서 만료 시간 추출 및 저장
      if (parsedToken?.exp != null) {
        await localforage.setItem('app.user.expiresIn', parsedToken.exp)
      }

      // 7. OfficeNext 플래그 설정
      await localforage.setItem('app.user.officenext', true)

      // 8. API URL 설정
      setApiURL()

      resolve(true)
    } catch (err) {
      console.error('loginByTokenFromDesktop error:', err)
      resolve(false)
    }
  })

  return promise
}

export function loginByEntryCheck(entryToken: string, entryPath?: string): Promise<boolean> {
  const promise = new Promise<boolean>(async (resolve) => {
    const token = await localforage.getItem('app.user.token')
    if (token != null) {
      const parsedEntryToken = decodeJwtPayload(entryToken)
      const parsedCommonToken = decodeJwtPayload(token as string)

      const decision = decideEntryTokenReuse(parsedEntryToken, parsedCommonToken, Date.now())

      if (decision === 'reuse') {
        await localforage.setItem('app.user.entryToken', entryToken)
        await localforage.setItem('app.user.entryPath', entryPath || '')

        setTempEnvParam('app.entry.state', true)
        resolve(true)
        return
      }

      // 다른 계정/손상 토큰('resetThenExchange')은 이전 세션을 정리한다(교차 사용자 데이터 오염 방지).
      // 동일 계정 만료('exchange')는 logout()을 호출하지 않아 saga 폴백과의 이중 dispatch를 막는다.
      if (decision === 'resetThenExchange') {
        await logout()
      }
    }
    resolve(false)
  })
  return promise
}

export function loginByEntry(entryToken: string): Promise<EntryLoginResult> {
  const promise = new Promise<EntryLoginResult>(async (resolve) => {
    if (!entryToken) {
      resolve('fail')
      return
    }

    authClient
      .post('/auth/issue-token/officewave', {
        provider: 'officenext',
        access_token: entryToken,
        service_group_code: 'officewave',
      })
      .then(async (response) => {
        if (response != null && response.data != null && response.data.data != null) {
          const { access_token, token_type, refresh_token } = response.data.data
          await localforage.setItem('app.user.token', access_token)
          await localforage.setItem('app.user.tokenType', token_type)
          await localforage.setItem('app.user.refreshToken', refresh_token)
          await localforage.setItem('app.user.jmap.tokenType', 'Bearer')
          await localforage.setItem('app.user.jmap.token', access_token)

          const parsedCommonToken = decodeJwtPayload(access_token)
          if (parsedCommonToken != null) {
            await localforage.setItem('app.user.expiresIn', parsedCommonToken.exp)
            const email = parsedCommonToken.email || parsedCommonToken.account
            setEnvParam('userEmail', email)
            setEnvParam('userInternalEmail', parsedCommonToken.sub || email)
            await localforage.setItem('app.user.workspace_id', parsedCommonToken.workspace_id)
          }

          await localforage.setItem('app.user.officenext', true)
          setApiURL()

          setTempEnvParam('app.entry.state', true)

          resolve('success')
        } else {
          console.error('loginByEntry: unexpected response format', response?.data)
          resolve('fail')
        }
      })
      .catch((err: unknown) => {
        console.error('loginByEntry: API error', err)
        // 409/400 에러는 응답 message를 사용자에게 노출하기 위해 객체로 전달, 그 외는 'fail'.
        resolve(classifyEntryError(err))
      })
  })

  return promise
}

export async function currentAccount(): Promise<CurrentAccountResult | null> {
  console.log(' --- currentAccount ')
  console.trace('[currentAccount] Called from:')

  if (getTempEnvParam('app.session.id') == null) {
    const newSessionId = crypto.randomUUID()
    sessionStorage.setItem('app.session.id', newSessionId)
    setTempEnvParam('app.session.id', newSessionId)
  }

  const sessionId = sessionStorage.getItem('app.session.id')

  const workspaceId = await localforage.getItem('app.user.workspace_id')

  setTempEnvParam('app.user.workspace_id', workspaceId)

  const token = await localforage.getItem('app.user.token')

  // popup(EmailEditorWindow)은 부모 탭의 토큰 변경 storage event 로 reload 되면 작성 내용이
  // 날아가므로 리스너 등록 자체를 스킵한다.
  const storageEventAttached = getTempEnvParam('storageEventAttached')
  if (!isPopupMode() && storageEventAttached !== true) {
    window.addEventListener('storage', (evt: StorageEvent) => {
      console.log('storage event', evt)
      if (evt.type === 'storage' && evt.storageArea === window.localStorage) {
        const { key, newValue, oldValue } = evt
        if (key != null && key.indexOf('app.user.token') > -1) {
          if (newValue == null && oldValue != null) {
            window.location.reload()
          } else if (newValue != null && oldValue == null) {
            window.location.reload()
          }
          if (newValue != null) {
            dataMgr.refreshJMAPAuthInfo(newValue)
          }
        }
      }
    })
    setTempEnvParam('storageEventAttached', true)
  }

  // popup 은 BroadcastChannel reload 명령으로 새로고침되지 않도록 등록을 스킵한다.
  const appMessageEventAttached = getTempEnvParam('appMessageEventAttached')
  if (!isPopupMode() && appMessageEventAttached !== true) {
    const onAppMessage = async (e: MessageEvent) => {
      console.log(' -- message : ', e)

      if (window.location.origin !== e.origin) {
        return
      }

      if (e.data != null && typeof e.data === 'string' && e.data.length > 0) {
        const data = JSON.parse(e.data)
        console.log(' -- message data : ', data)
        const { cmd, senderId } = data
        // 요청을 보낸 세션이 현재 세션이면 처리하지 않는다.
        // BroadcastChannel인 경우에는 자신에게는 메시지가 오지 않으므로, 검사하지 않아도 됨.
        if (senderId != null && senderId === sessionId) {
          return
        }
        if (cmd === 'reload') {
          window.location.reload()
        }
        // close는 자체적으로 불가능함.
      }
    }

    const channel = new BroadcastChannel('appMessageChannel')
    channel.onmessage = onAppMessage

    setTempEnvParam('appMessageEventAttached', true)
  }

  if (token == null) {
    reduxStore.dispatch({
      type: 'menu/RELOAD',
    })
    if (window.location.pathname.indexOf('/auth/') !== 0) {
      // 인증 처리 중일 떄는 로그아웃 처리 안함.
      reduxStore.dispatch({
        type: 'user/LOGOUT',
      })
    }
    return null
  } else if (window.location.pathname.indexOf('/auth/entry') === 0) {
    // /auth/entry 의 처리가 완료되지 않은 상태이면, currentAccount 처리 종료.
    const entryProcessed = getTempEnvParam('app.entry.state')
    if (entryProcessed !== true) {
      return null
    } else {
      removeTempEnvParam('app.entry.state')
    }
  } else if (window.location.pathname.indexOf('/auth/callback') === 0) {
    // /auth/callback의 처리가 완료되지 않은 상태이면, currentAccount 처리 종료.
    const callbackProcessed = getTempEnvParam('app.callback.state')
    if (callbackProcessed !== true) {
      return null
    } else {
      removeTempEnvParam('app.callback.state')
    }
  }

  const services: Services = {
    source: null,
    pages: {},
    ai: {},
    can_use_email: false,
  }

  const servicePreset: Record<string, ServicePreset | { enable_orgchart: boolean }> = {
    officewave: {
      pages: {
        email: false,
        contacts: false,
        calendar: true,
        todo: true,
      },
      crm_contacts: { editable_by_user: false },
      can_use_email: false,
    },
    officemail_desktop: {
      pages: {
        email: false,
        contacts: false,
        calendar: false,
        todo: false,
      },
      crm_contacts: { editable_by_user: false },
      can_use_email: false,
    },

    officemail_server: {
      pages: {
        email: true,
        contacts: true,
        calendar: true,
        todo: true,
      },
      crm_contacts: { editable_by_user: true },
      can_use_email: true,
    },
    org: {
      enable_orgchart: true,
    },
  }

  const applyMap = (target: any, value: any): void => {
    Object.keys(value).forEach((key) => {
      if (typeof value[key] === 'boolean') {
        target[key] = target[key] || value[key]
      } else if (typeof value[key] === 'object') {
        if (target[key] == null) {
          target[key] = {}
        }
        applyMap(target[key], value[key])
      }
    })
  }

  const isDesktopSession = NativeInterface.isAvailable()
  const parsedCommonToken: ParsedToken | null = decodeJwtPayload(token as string)
  let workspaceConfigEnabled: boolean = false
  let orgChartEnabled: boolean = false
  let sharedContactsEnabled: boolean = false
  if (parsedCommonToken != null && parsedCommonToken.services != null) {
    // setEnvParam('userEmail', parsedCommonToken.name)
    setEnvParam('userEmail', parsedCommonToken.email || parsedCommonToken.account)

    services.source = 'commonToken'
    sharedContactsEnabled = true

    parsedCommonToken.services.forEach((service) => {
      switch (service) {
        case 'officewave':
          workspaceConfigEnabled = workspaceConfigEnabled || false
          applyMap(services, servicePreset.officewave)
          break
        case 'officemail_server':
          workspaceConfigEnabled = true
          applyMap(services, servicePreset.officemail_server)
          break
        case 'org':
          orgChartEnabled = true
          applyMap(services, servicePreset.org)
          break
        default:
          // officewave가 아닌 경우에는 요청 가능.
          workspaceConfigEnabled = true
          break
      }
    })

    setTempEnvParam('app.services', services)
    reduxStore.dispatch({
      type: 'menu/RELOAD',
    })
  } else if (isDesktopSession) {
    workspaceConfigEnabled = false
    applyMap(services, servicePreset.officemail_desktop)
    setTempEnvParam('app.services', services)
    reduxStore.dispatch({
      type: 'menu/RELOAD',
    })
  } else {
    // 1. commonAuth token이 아닌 경우
    // 2. 데스크탑에서 rework token이 아닌 경우
    console.error('currentAccount: token parsing failed or missing services field')
    reduxStore.dispatch({
      type: 'menu/RELOAD',
    })
    // LOGOUT은 호출자(saga)에서 처리. 로그인 플로우 중에 LOGOUT이 실행되면
    // 토큰이 삭제되어 진행 중인 로그인이 중단될 수 있음.
    return null
  }

  let workspaceConfig: WorkspaceConfig | null = null
  if (workspaceId != null) {
    try {
      workspaceConfig = await getWorkspaceConfig(workspaceId as string)
      // console.log('workspaceConfig - ', workspaceConfig);
    } catch (e) {
      console.log('getWorkspaceConfig error', e)
    }
  }

  if (workspaceConfig != null) {
    if (workspaceConfigEnabled === false) {
      if (workspaceConfig != null && isDesktopSession !== true) {
        if (workspaceConfig.enable_officemail === true) {
          console.log('workspaceConfig - ', workspaceConfig)
          services.can_use_email = true
        }
      }
    } else {
      setTempEnvParam('workspaceConfig', workspaceConfig)

      services.source = 'workspaceConfig'
      services.can_use_email = workspaceConfig.enable_officemail || false
      services.pages.email = workspaceConfig.enable_officemail
      services.pages.contacts = true
      services.pages.calendar = true
      services.pages.todo = true
      services.enable_orgchart = workspaceConfig.enable_org_chart
      services.crm_contacts = workspaceConfig.crm_contacts
      services.shared_contacts = workspaceConfig.shared_contacts
      services.priority_inbox = workspaceConfig.priority_inbox

      services.ai = workspaceConfig.ai

      if (workspaceConfig.spam_sniper != null) {
        const { spam_folder } = workspaceConfig.spam_sniper
        if (spam_folder === true) {
          services.spam_sniper = true
        }
      }

      orgChartEnabled = services.enable_orgchart === true
      sharedContactsEnabled = services.shared_contacts === true

      setTempEnvParam('app.services', services)

      reduxStore.dispatch({
        type: 'menu/RELOAD',
      })
    }
  }

  const offlineUI = await checkOffline({
    onClickRetry: () => {
      window.location.reload()
    },
    autoReload: true,
  })

  // offline상태면 여기서 중단.
  if (offlineUI != null) {
    return
  }

  if (services.pages.email === true && !isPopupMode()) {
    // JMAP 로그인 처리를 위해 호출 필요.
    // popup(handoff-only)은 opener 세션/DataManager 를 재사용하므로 자체 JMAP 로그인은 스킵한다.
    await currentAccountJMAP()
  }

  // 사용자 정보는 기존의 rework 정보를 사용하도록 한다.
  let myInfo: MyInfo = {
    email: getEnvParam('userEmail'),
  }

  if (workspaceConfigEnabled) {
    try {
      myInfo = await getMyInfo()
      setEnvParam('userEmail', myInfo.email)
    } catch (e) {
      console.log('getMyInfo error', e)
    }
  }

  settings.init()

  // popup(handoff-only)은 자체 DataManager 를 부팅하지 않는다. opener 의 sendHelper.dataMgr 을
  // 그대로 사용하므로, popup 이 origin 공유 IndexedDB/캐시를 리셋(init)하거나 EventSource(firstLoad)를
  // 여는 부작용을 막는다. (CRITICAL 1: 메인 앱 아웃박스/캐시 유실 및 다중 EventSource 방지)
  if (!isPopupMode()) {
    dataMgr.init()
    dataMgr.setUserInfo(myInfo)
  }

  let name = myInfo.display_name
  if (orgChartEnabled === true && !isPopupMode()) {
    // 세션 전환 시 이전 세션의 저장 실패/서킷 브레이커 상태를 리셋한다(R9).
    // exchange(동일계정 토큰만료)/desktop-token 등 logout()을 거치지 않는 전환에서도
    // currentAccount는 반드시 경유하므로, 이전 세션 브레이커가 새 세션 로드를 막는 것을 방지한다.
    orgChartHelper.clearSaveFailure()
    await orgChartHelper.startLoading()
    const contactInfo = await orgChartHelper.findContact(myInfo.email)
    if (contactInfo) {
      name = contactInfo.name

      if (myInfo.display_name == null) {
        myInfo.display_name = name
      }
    }
  }

  // db에 저장된 데이터 로딩 (migration 이 필요한 경우, migration 수행 -> db update)
  // OMD-225 Phase 1: 로컬 로드(RELOAD Phase-1: DB 로드+SET_STATE)가 서버 로드(sync)보다 먼저
  // 완료됨을 보장하기 위해 onComplete 를 await 한다. (서버값을 로컬 기본값이 덮어쓰는 race 제거)
  type ReloadPhase1Result = {
    davMigratedCategories: { calendar: boolean; todo: boolean }
    signatureMigrated: boolean
    name?: string
    email?: string
  }
  const reloadResult = await new Promise<ReloadPhase1Result>((resolve) => {
    // 방어적 타임아웃: RELOAD saga 가 예외 등으로 onComplete 를 호출하지 못해도 로그인이 hang 되지 않도록 한다.
    const fallbackTimer = setTimeout(() => {
      resolve({
        davMigratedCategories: { calendar: false, todo: false },
        signatureMigrated: false,
        email: myInfo.email,
      })
    }, 10000)
    reduxStore.dispatch({
      type: 'settings/RELOAD_SETTINGS',
      payload: {
        name: name,
        email: myInfo.email,
        onComplete: (result: ReloadPhase1Result) => {
          clearTimeout(fallbackTimer)
          resolve(result)
        },
      },
    })
  })

  if (!isPopupMode()) {
    try {
      // server의 내용을 로컬에 동기화. popup 은 부모와 중복 GET 을 피하려 스킵한다.
      await settingSyncService.sync()
    } catch (e) {
      console.log('settingSyncService.sync error', e)
    }
  }

  // OMD-225 Phase 1(마무리): 서버 로드 이후, 조건부 쓰기(accountName/defaultSendAsAccount)와
  // DAV 마이그레이션 업로드를 "서버값이 반영된 현재 Redux" 기준으로 재평가해 수행한다.
  reduxStore.dispatch({
    type: 'settings/RELOAD_SETTINGS_FINALIZE',
    payload: reloadResult,
  })

  if (sharedContactsEnabled === true && workspaceId != null && !isPopupMode()) {
    contactHelper.initHelper(workspaceId as string, myInfo.email)
  }

  // popup(handoff-only)은 캘린더/연락처 클라이언트 초기화도 스킵한다(opener 와 중복, 공유 저장소 접근).
  if (!isPopupMode()) {
    if (services.pages.calendar === true || services.pages.todo === true) {
      await initCalendarClient(myInfo)
    }
    if (services.pages.contacts === true) {
      try {
        await initContactClient(myInfo)
      } catch (e) {
        console.error('initContactClient error', e)
      }
    }
  }

  // popup(handoff-only)은 alias/서명/firstLoad(EventSource) 등 이메일 서비스 부팅을 스킵한다.
  // 발송/작성에 필요한 서명/데이터는 opener 의 sendHelper.dataMgr 을 통해 접근한다.
  if (services.pages.email === true && !isPopupMode()) {
    const userDomain = myInfo.email.split('@')[1]
    const { aliases, groupmails } = await mailboxApiHelper.getAliasesAndGroupMails(userDomain)
    console.log('alias, groupmails', aliases, groupmails)
    reduxStore.dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        'localSettings.email.sendAsAccounts': aliases,
        'localSettings.email.groupMails': groupmails,
      },
    })
    signatureAPI.setUserInfo(myInfo)
    dataMgr.setSignatureAPI(signatureAPI)
    await signatureAPI.open()
    dataMgr.firstLoad()
  }

  let workspaceInfo: WorkspaceInfo = { role: '' }
  if (workspaceConfigEnabled) {
    try {
      const info = await getWorkspaceInfo(workspaceId as string)
      workspaceInfo = { role: info.role || '', ...info }
      console.log('workspaceInfo', workspaceInfo)
    } catch (e) {
      console.log('getWorkspaceInfo error', e)
    }
  }

  const avatarUrl = myInfo.avatar_url != null ? myInfo.avatar_url : ''

  // 로그인이 성공하면 entryToken과 관련 정보 삭제.
  await localforage.removeItem('app.user.entryToken')

  sentrySetUser(myInfo.email, myInfo.id)

  return {
    id: myInfo.id,
    name: myInfo.display_name || '',
    email: myInfo.email,
    uid: '',
    avatar: avatarUrl,
    role: workspaceInfo.role,
    lastLogin: '',
    workspace: workspaceInfo,
  }
}

// OfficeNEXT 세션 로그아웃. (OFM-242)
// - instantToken 이 있으면 그 토큰을 app.user.providerToken 에 저장해 그 계정으로 로그아웃한다
//   (officeNextClient 가 app.user.providerToken 을 Bearer 로 주입 — myNet.ts:294-304).
// - instantToken 이 없으면 로컬 저장 토큰으로 로그아웃(기존 logout 동작).
// setItem → post → removeItem 순서를 보장한다.
export async function logoutOfficeNext(
  instantToken: string | null = null,
  forced: boolean = false,
): Promise<void> {
  if (instantToken != null) {
    await localforage.setItem('app.user.providerToken', instantToken)
  }
  const providerToken = await localforage.getItem('app.user.providerToken')
  if (providerToken != null || forced === true) {
    const syncAniObj = openloadingAni({
      text: i18n.t('logOut.desc.progress'),
    })

    try {
      const headers = {
        Accept: 'application/json',
      }
      await officeNextClient.post('/api/logout', {}, { headers })
    } catch (e) {
      console.log('logout error', e)
    }

    syncAniObj.destroy()
  }

  await localforage.removeItem('app.user.providerToken')
}

export async function logout(forced: boolean = false): Promise<boolean> {
  // const officenext = await localforage.getItem('app.user.officenext')
  await logoutOfficeNext(null, forced)

  // await localforage.removeItem('app.user.appconfig')

  await localforage.removeItem('app.user.provisioning')
  await localforage.removeItem('app.user.officenext')
  await localforage.removeItem('app.user.connection_digest')
  await localforage.removeItem('app.user.token')
  await localforage.removeItem('app.user.tokenType')
  await localforage.removeItem('app.user.jmap.tokenType')
  await localforage.removeItem('app.user.jmap.token')
  await localforage.removeItem('app.user.expiresIn')
  await localforage.removeItem('app.user.refreshToken')
  await localforage.removeItem('app.user.refreshExpiresIn')
  await localforage.removeItem('app.user.workspace_id')
  await localforage.removeItem('app.user.deviceUUID')
  await localforage.removeItem('app.user.fcmToken')
  await localforage.removeItem('app.user.fcmToken_date')
  await localforage.removeItem('app.user.registorInfo')
  await localforage.removeItem('app.user.isDesktopSession')
  await localforage.removeItem('app.device.uuid')

  // await localforage.removeItem('app.user.email')

  // // 사용하지 않는 key 이지만, 이전의 데이터를 지우기 위해 남겨놓음.
  // await localforage.removeItem('app.user.workspace')

  removeTempEnvParam('workspaceConfig')
  removeTempEnvParam('app.services')
  removeTempEnvParam('app.entry.state')
  removeTempEnvParam('app.callback.state')

  removeEnvParam('provider')
  removeEnvParam('userEmail')
  removeEnvParam('userInternalEmail')
  removeEnvParam('appType')
  removeEnvParam('apiURL')

  reduxStore.dispatch({
    type: 'settings/RELOAD_SETTINGS',
    payload: {
      name: null,
      email: null,
    },
  })

  dataMgr.clear()
  settings.clear()

  await settingSyncService.clear()

  await logoutJMAP()

  // 조직도 싱글톤 상태 리셋(교차계정/서킷 브레이커 잔존 방지). orgChartDB의 실제 clear는
  // 위 dataMgr.clear()가 이미 담당하므로 여기선 DB I/O 없이 상태만 리셋한다(동기, throw/hang 불가).
  orgChartHelper.logout()

  await logoutContact()
  await logoutCalendar()
  await logoutTodo()

  if (forced !== true) {
    const entryToken = await localforage.getItem('app.user.entryToken')
    if (entryToken != null) {
      const entryPath = await localforage.getItem('app.user.entryPath')
      reduxStore.dispatch({
        type: 'user/LOGIN_BY_ENTRY',
        payload: {
          token: entryToken,
          path: entryPath,
        },
      })
    }
  }

  await localforage.removeItem('app.user.entryToken')
  await localforage.removeItem('app.user.entryPath')

  return true
}

const apis = {
  // customFetch,
  login,
  logout,
}

export default apis
